import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Globe, FileText, Sparkles, BarChart3, HelpCircle, CreditCard, Users, Zap, Loader2, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const LANDING_SECTIONS = [
  { key: 'hero', label: 'Hero (Ana Bölüm)', icon: Sparkles, fields: [
    { name: 'badge', label: 'Badge Metni', placeholder: 'Yapay Zeka Destekli...' },
    { name: 'title_1', label: 'Başlık (1. satır)', placeholder: 'İçerik' },
    { name: 'title_highlight', label: 'Başlık (vurgulu)', placeholder: 'Oluştur, Otomatikleştir' },
    { name: 'title_2', label: 'Başlık (son kısım)', placeholder: 've Ölçeklendir' },
    { name: 'subtitle', label: 'Alt Başlık', placeholder: 'Metin, görsel, video...' },
    { name: 'cta_primary', label: 'Ana Buton', placeholder: 'Ücretsiz Başla' },
    { name: 'cta_secondary', label: 'İkinci Buton', placeholder: 'Demo İzle' },
  ]},
  { key: 'social_proof', label: 'Sosyal Kanıt', icon: Users, fields: [
    { name: 'tagline', label: 'Başlık', placeholder: "1.000'den fazla..." },
    { name: 'testimonial_1_name', label: '1. Yorum - İsim' },
    { name: 'testimonial_1_role', label: '1. Yorum - Rol' },
    { name: 'testimonial_1_text', label: '1. Yorum - Metin', multiline: true },
    { name: 'testimonial_2_name', label: '2. Yorum - İsim' },
    { name: 'testimonial_2_role', label: '2. Yorum - Rol' },
    { name: 'testimonial_2_text', label: '2. Yorum - Metin', multiline: true },
    { name: 'testimonial_3_name', label: '3. Yorum - İsim' },
    { name: 'testimonial_3_role', label: '3. Yorum - Rol' },
    { name: 'testimonial_3_text', label: '3. Yorum - Metin', multiline: true },
  ]},
  { key: 'pricing', label: 'Fiyatlandırma', icon: CreditCard, fields: [
    { name: 'title', label: 'Bölüm Başlığı' },
    { name: 'subtitle', label: 'Alt Başlık' },
    { name: 'plan1_name', label: 'Plan 1 - Ad' },
    { name: 'plan1_price', label: 'Plan 1 - Fiyat' },
    { name: 'plan1_desc', label: 'Plan 1 - Açıklama' },
    { name: 'plan1_features', label: 'Plan 1 - Özellikler (virgülle ayır)', multiline: true },
    { name: 'plan2_name', label: 'Plan 2 - Ad' },
    { name: 'plan2_price', label: 'Plan 2 - Fiyat' },
    { name: 'plan2_desc', label: 'Plan 2 - Açıklama' },
    { name: 'plan2_features', label: 'Plan 2 - Özellikler (virgülle ayır)', multiline: true },
    { name: 'plan3_name', label: 'Plan 3 - Ad' },
    { name: 'plan3_price', label: 'Plan 3 - Fiyat' },
    { name: 'plan3_desc', label: 'Plan 3 - Açıklama' },
    { name: 'plan3_features', label: 'Plan 3 - Özellikler (virgülle ayır)', multiline: true },
  ]},
  { key: 'faq', label: 'SSS (Sık Sorulan Sorular)', icon: HelpCircle, fields: [
    { name: 'faq_1_q', label: 'Soru 1' },
    { name: 'faq_1_a', label: 'Cevap 1', multiline: true },
    { name: 'faq_2_q', label: 'Soru 2' },
    { name: 'faq_2_a', label: 'Cevap 2', multiline: true },
    { name: 'faq_3_q', label: 'Soru 3' },
    { name: 'faq_3_a', label: 'Cevap 3', multiline: true },
    { name: 'faq_4_q', label: 'Soru 4' },
    { name: 'faq_4_a', label: 'Cevap 4', multiline: true },
    { name: 'faq_5_q', label: 'Soru 5' },
    { name: 'faq_5_a', label: 'Cevap 5', multiline: true },
  ]},
];

const STATIC_PAGES = [
  { key: 'about', label: 'Hakkımızda', icon: Globe },
  { key: 'privacy', label: 'Gizlilik Politikası', icon: FileText },
  { key: 'terms', label: 'Kullanım Koşulları', icon: FileText },
  { key: 'cookies', label: 'Çerez Politikası', icon: FileText },
];

function SectionEditor({ section, savedContent, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(savedContent || {});
    setSaved(false);
  }, [savedContent, section.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(section.key, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error handled in parent
    }
    setSaving(false);
  };

  const handleReset = () => {
    setForm(savedContent || {});
    setSaved(false);
  };

  return (
    <Card className="bg-[#141414] border-[#262626]">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-outfit text-base text-white flex items-center gap-2">
            <section.icon size={18} className="text-violet-400" />
            {section.label}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-[#737373] hover:text-white h-8 text-xs" data-testid={`reset-${section.key}`}>
              <RotateCcw size={12} className="mr-1" /> Sıfırla
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs" data-testid={`save-${section.key}`}>
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : saved ? <Check size={12} className="mr-1" /> : <Save size={12} className="mr-1" />}
              {saved ? 'Kaydedildi' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {section.fields.map(field => (
          <div key={field.name}>
            <Label className="text-[#A3A3A3] font-ibm text-xs">{field.label}</Label>
            {field.multiline ? (
              <textarea
                value={form[field.name] || ''}
                onChange={e => setForm({ ...form, [field.name]: e.target.value })}
                placeholder={field.placeholder}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white mt-1 rounded-md p-2.5 min-h-[80px] text-sm font-ibm focus:outline-none focus:border-violet-500/50"
                data-testid={`field-${section.key}-${field.name}`}
              />
            ) : (
              <Input
                value={form[field.name] || ''}
                onChange={e => setForm({ ...form, [field.name]: e.target.value })}
                placeholder={field.placeholder}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1 h-9 text-sm"
                data-testid={`field-${section.key}-${field.name}`}
              />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PageEditor({ page, savedContent, onSave }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTitle(savedContent?.title || '');
    setBody(savedContent?.body || '');
    setSaved(false);
  }, [savedContent, page.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(`page_${page.key}`, { title, body });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  return (
    <Card className="bg-[#141414] border-[#262626]">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-outfit text-base text-white flex items-center gap-2">
            <page.icon size={18} className="text-blue-400" />
            {page.label}
            <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20 ml-2">
              /sayfa/{page.key}
            </Badge>
          </CardTitle>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white h-8 text-xs" data-testid={`save-page-${page.key}`}>
            {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : saved ? <Check size={12} className="mr-1" /> : <Save size={12} className="mr-1" />}
            {saved ? 'Kaydedildi' : 'Kaydet'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-[#A3A3A3] font-ibm text-xs">Sayfa Başlığı</Label>
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={page.label}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1 h-9 text-sm"
            data-testid={`page-title-${page.key}`}
          />
        </div>
        <div>
          <Label className="text-[#A3A3A3] font-ibm text-xs">Sayfa İçeriği (HTML destekler)</Label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Sayfa içeriğini buraya yazın... HTML etiketleri kullanabilirsiniz."
            className="w-full bg-[#0A0A0A] border border-[#262626] text-white mt-1 rounded-md p-3 min-h-[250px] text-sm font-ibm focus:outline-none focus:border-violet-500/50 leading-relaxed"
            data-testid={`page-body-${page.key}`}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function SiteManagementPage() {
  const [allContent, setAllContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/site-content').then(r => {
      setAllContent(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (section, content) => {
    try {
      await api.put(`/site-content/${section}`, { section, content });
      setAllContent(prev => ({ ...prev, [section]: content }));
      toast.success('Kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
      throw err;
    }
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm p-8">Yükleniyor...</div>;

  return (
    <div data-testid="site-management-page" className="animate-fade-in space-y-6">
      <div className="pb-6 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Site Yönetimi</h1>
        <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Landing page içerikleri ve statik sayfaları düzenleyin</p>
      </div>

      <Tabs defaultValue="landing" className="w-full">
        <TabsList className="bg-[#141414] border border-[#262626] p-1 h-auto">
          <TabsTrigger value="landing" className="text-sm font-ibm data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="tab-landing">
            <Globe size={14} className="mr-1.5" /> Landing Page
          </TabsTrigger>
          <TabsTrigger value="pages" className="text-sm font-ibm data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="tab-pages">
            <FileText size={14} className="mr-1.5" /> Sayfalar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="landing" className="mt-6 space-y-4">
          {LANDING_SECTIONS.map(section => (
            <SectionEditor
              key={section.key}
              section={section}
              savedContent={allContent[section.key]}
              onSave={handleSave}
            />
          ))}
        </TabsContent>

        <TabsContent value="pages" className="mt-6 space-y-4">
          {STATIC_PAGES.map(page => (
            <PageEditor
              key={page.key}
              page={page}
              savedContent={allContent[`page_${page.key}`]}
              onSave={handleSave}
            />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
